import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, Dropdown, DropdownMenu, DropdownToggle } from "reactstrap";
import { CurrentUserRole } from "../../api/auth";
import { CurrentSiteIdGet } from "../../api/userSettings";
import { roles } from "../../config";
import { smsAllowanceDetail, smsAllowanceExtraText, smsAllowanceHeadline, smsAllowanceRemainingText, smsAllowanceRenewsText, smsAllowanceTextClass } from "../../helpers/smsAllowance";
import useSmsAllowance from "../../hooks/useSmsAllowance";
import local from "../../localization/strings";
import FalconCardHeader from "../common/FalconCardHeader";
import SmsAllowanceLogModal from "./SmsAllowanceLogModal";
import SmsAllowanceMeter from "./SmsAllowanceMeter";

// The site's cloud-SMS allowance in the top navigation bar, beside the notifications bell: a short bar
// and the figure, with a status word when the status is anything but normal. Pressing it opens the
// detail — what is left, when the free allowance renews, what the segments pay for and who to ask for
// more — and from there the log of every text the cloud has sent for the site.
//
// Renders nothing for a site with no cloud SMS at all, so an email-only site never sees it.
const SmsAllowanceChip = () => {
	const { view } = useSmsAllowance();
	const [isOpen, setIsOpen] = useState(false);
	const [logOpen, setLogOpen] = useState(false);

	if (!view || view.status === "unused") return null;

	const stopped = view.status === "off" || view.status === "exhausted";
	const textClass = smsAllowanceTextClass(view.status);
	const headline = smsAllowanceHeadline(view);
	const detail = smsAllowanceDetail(view);
	const renews = smsAllowanceRenewsText(view);
	const extra = smsAllowanceExtraText(view);
	// Eltek and distributors both add packages, but each in their own area: /admin is Eltek-only and
	// /distributor is distributor-only, so the role is matched exactly -- the hierarchy check lets a
	// distributor through to an /admin link that then 403s. Everyone else asks their distributor.
	const userRole = CurrentUserRole();
	const packages = userRole === roles.Eltek ? { to: "/admin", text: local.TF_SmsAllowanceManagePackages } : userRole === roles.Distributor ? { to: "/distributor", text: local.TF_SmsAllowanceAddPackage } : null;

	const openLog = (e) => {
		e.preventDefault();
		setIsOpen(false);
		setLogOpen(true);
	};

	return (
		<>
			<Dropdown nav inNavbar isOpen={isOpen} toggle={() => setIsOpen(!isOpen)}>
				<DropdownToggle nav className="px-2 d-flex align-items-center" title={`${local.TF_SmsAllowance}: ${headline}`} aria-label={`${local.TF_SmsAllowance}: ${smsAllowanceRemainingText(view)}. ${headline}`}>
					<FontAwesomeIcon icon={stopped ? "comment-slash" : "comment"} className={`${textClass} mr-2`} fixedWidth />
					<span className="d-none d-md-inline text-uppercase fs--2 text-600 mr-2">{local.TF_SmsAllowance}</span>
					<SmsAllowanceMeter view={view} className="d-none d-sm-flex mr-2" style={{ width: 64 }} />
					<span className="text-nowrap">{view.remaining.toLocaleString()}</span>
					{view.status !== "normal" && <span className={`d-none d-lg-inline ml-2 ${textClass}`}>{headline}</span>}
				</DropdownToggle>
				<DropdownMenu right className="dropdown-menu-card">
					<Card className="card-notification shadow-none" style={{ width: "22rem", maxWidth: "90vw" }}>
						<FalconCardHeader className="card-header" title={local.TF_SmsAllowance} titleTag="h6" light={false} />
						<div className="p-3 font-weight-normal fs--1">
							<div className="d-flex align-items-baseline">
								<span className="fs-2 font-weight-semi-bold text-900 mr-2">{view.remaining.toLocaleString()}</span>
								<span className="text-600">{local.TF_SmsAllowanceSegments}</span>
							</div>
							<SmsAllowanceMeter view={view} className="my-2" />
							<div className={`font-weight-semi-bold ${textClass}`}>{headline}</div>
							{detail && <p className="mb-0 mt-2 text-600">{detail}</p>}
							{renews && <p className="mb-0 mt-2 text-600">{renews}</p>}
							{extra && <p className="mb-0 mt-1 text-600">{extra}</p>}

							<hr className="my-3" />

							<p className="mb-2 text-600">{local.TF_SmsExplainerSummary}</p>
							{packages ? (
								<p className="mb-0 text-600">
									<Link to={packages.to} onClick={() => setIsOpen(false)}>
										{packages.text}
									</Link>
								</p>
							) : (
								<p className="mb-0 text-600">{local.TF_SmsAllowanceHowToAdd}</p>
							)}

							<Button color="primary" size="sm" block className="mt-3" onClick={openLog}>
								<FontAwesomeIcon icon="list-alt" className="mr-2" />
								{local.TF_SmsAllowanceViewLog}
							</Button>
						</div>
					</Card>
				</DropdownMenu>
			</Dropdown>

			<SmsAllowanceLogModal isOpen={logOpen} toggle={() => setLogOpen(false)} siteId={CurrentSiteIdGet()} view={view} />
		</>
	);
};

export default SmsAllowanceChip;
